import Avatar from '@/components/linear/avatar';
import PriorityIcon from '@/components/linear/priority-icon';
import StatusIcon from '@/components/linear/status-icon';
import { useWorkspaceChrome } from '@/components/linear/workspace-chrome';
import {
  issueAssigneePath,
  issueLabelPath,
  issueLabelsPath,
  issuePriorityPath,
  issueStatusPath,
} from '@/lib/linear-paths';
import { PRIORITY_LABELS, STATUS_LABELS } from '@/lib/linear-utils';
import type { IssueStatus } from '@/types/issue';
import type { SharedPageProps } from '@/types/shared';
import { router, usePage } from '@inertiajs/react';

export default function ShortcutMenu() {
  const { shortcutMenu, setShortcutMenu, selectedIssue } = useWorkspaceChrome();
  const { members = [], labels: workspaceLabels = [] } = usePage<
    SharedPageProps & {
      members?: Array<{ id: string; name: string; avatarColor?: string }>;
      labels?: Array<{ id: string; name: string; color: string }>;
    }
  >().props;
  const applied = selectedIssue?.labels ?? [];

  if (!shortcutMenu || !selectedIssue) return null;

  const close = () => setShortcutMenu(null);

  return (
    <div
      className="ln-modal-backdrop"
      style={{ alignItems: 'flex-start', paddingTop: '20vh', background: 'transparent' }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div className="ln-popover" style={{ position: 'relative', minWidth: 240, display: 'block' }}>
        {shortcutMenu === 'status'
          ? (Object.entries(STATUS_LABELS) as Array<[IssueStatus, string]>).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className="ln-popover-item"
                onClick={() => {
                  router.patch(
                    issueStatusPath(selectedIssue.id),
                    { status: value },
                    { preserveScroll: true }
                  );
                  close();
                }}
              >
                <StatusIcon status={value} />
                {label}
              </button>
            ))
          : null}
        {shortcutMenu === 'priority'
          ? Object.entries(PRIORITY_LABELS).map(([value, label]) => (
              <button
                key={value}
                type="button"
                className="ln-popover-item"
                onClick={() => {
                  router.patch(
                    issuePriorityPath(selectedIssue.id),
                    { priority: Number(value) },
                    { preserveScroll: true }
                  );
                  close();
                }}
              >
                <PriorityIcon priority={Number(value)} />
                {label}
              </button>
            ))
          : null}
        {shortcutMenu === 'assignee' ? (
          <>
            <button
              type="button"
              className="ln-popover-item"
              onClick={() => {
                router.patch(
                  issueAssigneePath(selectedIssue.id),
                  { assigneeId: null },
                  { preserveScroll: true }
                );
                close();
              }}
            >
              Unassigned
            </button>
            {members.map((member) => (
              <button
                key={member.id}
                type="button"
                className="ln-popover-item"
                onClick={() => {
                  router.patch(
                    issueAssigneePath(selectedIssue.id),
                    { assigneeId: member.id },
                    { preserveScroll: true }
                  );
                  close();
                }}
              >
                <Avatar name={member.name} color={member.avatarColor} size={16} />
                {member.name}
              </button>
            ))}
          </>
        ) : null}
        {shortcutMenu === 'label' ? (
          workspaceLabels.length === 0 ? (
            <div className="ln-popover-item" style={{ cursor: 'default', opacity: 0.7 }}>
              No labels in this workspace
            </div>
          ) : (
            workspaceLabels.map((label) => {
              const on = applied.some((item) => item.id === label.id);
              return (
                <button
                  key={label.id}
                  type="button"
                  className="ln-popover-item"
                  data-active={on ? 'true' : 'false'}
                  onClick={() => {
                    if (on) {
                      router.delete(issueLabelPath(selectedIssue.id, label.id), {
                        preserveScroll: true,
                      });
                    } else {
                      router.post(
                        issueLabelsPath(selectedIssue.id),
                        { labelId: label.id },
                        { preserveScroll: true }
                      );
                    }
                    close();
                  }}
                >
                  <span
                    className="ln-label"
                    style={{ color: label.color, background: `${label.color}22` }}
                  >
                    {label.name}
                  </span>
                </button>
              );
            })
          )
        ) : null}
      </div>
    </div>
  );
}
